import type { EChartsOption } from 'echarts';
import { ChartDataResponse } from '../models/pergunta.models';

/** Paleta do design system InfoGOV (verdes → teais → azuis). */
export const CHART_PALETTE = [
  'hsl(152 76% 42%)',
  'hsl(168 72% 40%)',
  'hsl(184 70% 42%)',
  'hsl(198 72% 48%)',
  'hsl(145 60% 50%)',
  'hsl(160 65% 35%)',
  'hsl(176 60% 45%)',
  'hsl(210 70% 55%)',
];

/**
 * Converte a resposta bruta da API (`chartData` + `yAxisName`) em uma
 * `EChartsOption`. Porte enxuto de `ChartUtil.transformEcharts` do
 * infogov-front-17: escala valores para milhares/milhões e trata o tipo
 * `barpercent`.
 */
export function transformEcharts(
  chartData: ChartDataResponse,
  yAxisName = '',
  locale: 'pt-BR' | 'en' = 'pt-BR',
): EChartsOption {
  const series = chartData?.series;
  if (!series) {
    throw new Error('chartData.series indefinido');
  }
  const isPt = locale === 'pt-BR';
  const addPercent = series.some((r) => r.type === 'barpercent');

  if (addPercent) {
    // `barpercent`: as barras representam a participação (% do total da série).
    // Converte os valores para 0–100, para que a altura das barras e o eixo Y
    // fiquem em percentual (sem o escalonamento de milhares/milhões).
    series.forEach((r) => {
      const total = r.data.reduce(
        (sum, v) => sum + (v == null ? 0 : Number(v)),
        0,
      );
      r.type = 'bar';
      r.data = r.data.map((v) =>
        v == null ? v : total > 0 ? (Number(v) / total) * 100 : 0,
      );
    });
    yAxisName = isPt ? 'Percentual' : 'Percentage';
  } else {
    const over100Entries =
      (chartData.xAxis?.length ?? 0) > 100 || series.length > 100;
    const inMillions = series.some((r) => r.data.some((d) => d > 1_000_000));
    const inThousands = series.some((r) => r.data.some((d) => d > 1_000));

    // Os valores podem vir como string ou null (gaps da série histórica);
    // preserva null para manter as lacunas no gráfico de linha.
    const scale = (factor: number) => (d: number) =>
      d == null ? d : (d as number) / factor;

    if (!over100Entries) {
      if (inMillions) {
        series.forEach((r) => (r.data = r.data.map(scale(1_000_000))));
        yAxisName += isPt ? ' (em milhões)' : ' (in millions)';
      } else if (inThousands) {
        series.forEach((r) => (r.data = r.data.map(scale(1_000))));
        yAxisName += isPt ? ' (em milhares)' : ' (in thousands)';
      }
    }
  }

  const numberLocale = isPt ? 'pt-BR' : 'en-US';
  const axisLabelColor = 'hsl(215 16% 47%)';
  const gridLineColor = 'hsl(214 20% 90%)';

  // Uma única série (ex.: distribuição por categoria) não precisa de legenda com
  // o nome da métrica — as categorias já estão no eixo X. Nesse caso, colorimos
  // cada barra por categoria, como no protótipo.
  const singleSeries = series.length === 1;

  return {
    color: CHART_PALETTE,
    textStyle: { fontFamily: 'Georama, system-ui, sans-serif' },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255,255,255,0.96)',
      borderColor: gridLineColor,
      borderWidth: 1,
      padding: [10, 12],
      textStyle: { color: 'hsl(222 15% 20%)', fontSize: 12 },
      valueFormatter: (value) =>
        value != null
          ? Intl.NumberFormat(numberLocale, {
              maximumFractionDigits: addPercent ? 1 : 2,
            }).format(value as number) + (addPercent ? '%' : '')
          : '-',
      order: 'valueDesc',
    },
    grid: {
      left: 12,
      right: 20,
      bottom: singleSeries ? 12 : 48,
      top: 24,
      containLabel: true,
    },
    legend: singleSeries
      ? { show: false }
      : {
          bottom: 0,
          type: 'scroll',
          icon: 'roundRect',
          itemWidth: 10,
          itemHeight: 10,
          textStyle: { color: axisLabelColor, fontSize: 12 },
          data: series.map((r) => r.name).sort(),
        },
    xAxis: {
      type: 'category',
      data: chartData.xAxis,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: axisLabelColor, fontSize: 11 },
    },
    yAxis: {
      type: 'value',
      name: yAxisName,
      nameGap: 50,
      nameLocation: 'middle',
      nameTextStyle: { color: axisLabelColor, fontSize: 11 },
      max: addPercent ? 100 : undefined,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: {
        lineStyle: { color: gridLineColor, type: 'dashed' },
      },
      axisLabel: {
        color: axisLabelColor,
        fontSize: 11,
        formatter: (value: number) => {
          if (addPercent) return value + '%';
          if (value >= 1_000_000) return value / 1_000_000 + 'Mi';
          if (value >= 1_000) return value / 1_000 + 'Mil';
          return String(value);
        },
      },
    },
    series: series.map((r) => {
      const isBar = (r.type as string) === 'bar';
      const colorPerCategory = singleSeries && isBar;
      return {
        name: r.name,
        type: r.type as 'bar' | 'line',
        data: colorPerCategory
          ? r.data.map((value, i) => ({
              value,
              itemStyle: { color: CHART_PALETTE[i % CHART_PALETTE.length] },
            }))
          : r.data,
        smooth: r.type === 'line',
        showSymbol: false,
        lineStyle: r.type === 'line' ? { width: 2.5 } : undefined,
        itemStyle: isBar ? { borderRadius: [4, 4, 0, 0] } : undefined,
        barMaxWidth: 40,
        // Os dados de `barpercent` já foram convertidos para % — rotula direto.
        label: addPercent
          ? {
              show: true,
              position: 'top',
              color: axisLabelColor,
              fontSize: 11,
              formatter: (a: any) => `${Math.round(Number(a.value))}%`,
            }
          : undefined,
      };
    }),
  };
}

/** Gera CSV simples a partir da resposta (eixo X + séries). */
export function chartToCsv(
  chartData: ChartDataResponse,
  locale: 'pt-BR' | 'en' = 'pt-BR',
): string {
  const isPt = locale === 'pt-BR';
  const header =
    (isPt ? 'EixoX;' : 'X-Axis;') +
    chartData.series.map((s) => s.name).join(';');
  const rows = (chartData.xAxis ?? []).map((x, i) => {
    const cols = chartData.series.map((s) => s.data[i] ?? '');
    return `${String(x).replace(/\n/g, ' ')};${cols.join(';')}`;
  });
  return [header, ...rows].join('\r\n');
}
